import { TranslateService } from '@ngx-translate/core';
import { FeatherModule } from 'angular-feather';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  inject,
  Injector,
  input,
  output,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgControl,
} from '@angular/forms';

import { getErrorMessage } from '../../utils/validators';
import { TooltipComponent } from '../tooltip/tooltip.component';

@Component({
  selector: 'lib-input',
  imports: [FeatherModule, CommonModule, TooltipComponent],
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
})
export class InputComponent implements ControlValueAccessor, AfterViewInit {
  @ViewChild('inputElement', { static: false }) inputElement!: ElementRef<HTMLInputElement>;

  readonly id = input(`input-${Math.random().toString(36).substring(2, 8)}`);
  readonly type = input<'text' | 'email' | 'password' | 'number'>('text');
  readonly maxlength = input<number | null>(null);
  readonly label = input<string>();
  readonly prefix = input<string>('');
  readonly suffix = input<string>('');
  readonly placeholder = input<string>();
  readonly shortcut = input<string>();
  readonly required = input<boolean>();
  readonly rounded = input<boolean>(false);
  readonly autofocus = input<boolean>(false);
  readonly autocomplete = input<string>('off');
  readonly disabled = input(false);
  readonly customErrors = input<Record<string, string>>({});
  readonly tooltip = input<string>();
  readonly readOnly = input<boolean>(false);
  // Track disabled state coming from Angular forms API
  isDisabled = false;
  readonly value = input<string | null | undefined>(null);
  readonly handleFocus = output<void>();
  readonly handleBlur = output<void>();
  readonly inputChange = output<string>();
  readonly handlePaste = output<ClipboardEvent>();

  internalValue = '';
  ngControl: NgControl | null = null;

  private readonly translate = inject(TranslateService);
  private readonly injector = inject(Injector);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange = (_value: string) => {
    /* Implemented by Angular forms */
  };
  onTouched = () => {
    /* Implemented by Angular forms */
  };

  get displayValue(): string {
    // Use external value prop if provided, otherwise use internal value from forms
    return this.value() || this.internalValue;
  }

  writeValue(value: string): void {
    this.internalValue = value || '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  ngAfterViewInit() {
    // Safely get NgControl to avoid circular dependency
    try {
      this.ngControl = this.injector.get(NgControl, null);
    } catch {
      this.ngControl = null;
    }

    if (this.autofocus()) {
      Promise.resolve().then(() => this.focus());
    }
  }

  handleInput(event: Event): void {
    if (this.readOnly()) {
      return;
    }
    const target = event.target as HTMLInputElement;
    this.internalValue = target.value;
    this.onChange(this.internalValue);
    this.inputChange.emit(this.internalValue);
  }

  onBlur(): void {
    if (this.readOnly() || this.disabled() || this.isDisabled) {
      return;
    }
    this.onTouched();
    this.handleBlur.emit();
  }

  onPaste(event: ClipboardEvent): void {
    if (this.readOnly() || this.disabled() || this.isDisabled) {
      return;
    }
    this.handlePaste.emit(event);
  }

  focus(): void {
    if (this.inputElement) {
      this.inputElement.nativeElement.focus();
    }
  }

  get isRequired(): boolean {
    // First check explicit required input
    if (this.required()) {
      return true;
    }

    // Then check if form control has required validator
    const control = this.ngControl?.control;
    if (!control) {
      return false;
    }

    // Check if the control has a required validator
    const validator = control.validator;
    if (!validator) {
      return false;
    }

    // Run validator with empty value to check for required error
    const validationResult = validator({ value: null } as AbstractControl);
    return validationResult?.['required'] === true;
  }

  get showError(): boolean {
    const control = this.ngControl?.control;
    if (!control || !control.errors) {
      return false;
    }

    // Only show error if there's an error other than 'greylistedDomain'
    const hasRealError = Object.keys(control.errors).some((key) => key !== 'greylistedDomain');

    // Show errors immediately for populated fields, or after user interaction for empty fields
    const hasValue = control.value && control.value.toString().trim().length > 0;
    return hasRealError && (hasValue || control.touched || control.dirty);
  }

  getErrorMessage(): string {
    const control = this.ngControl?.control;
    if (!control || !control.errors) {
      return '';
    }

    const errorKey = Object.keys(control.errors)[0];
    const customErrorMap = this.customErrors();

    if (customErrorMap && customErrorMap[errorKey]) {
      return this.translate.instant(customErrorMap[errorKey]);
    }

    return getErrorMessage(control, this.translate);
  }
}
